import { useState } from "react";
import { useAccountContext } from "./useAccountContext";
import { useAuthContext } from "./useAuthContext";
import { useSettingsContext } from "./useSettingsContext";
import axios from 'axios';

// * Language 
import { useTranslation } from 'react-i18next';

export const useClients = () => {

    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { dispatch } = useAccountContext();
    const { isWinAuth } = useSettingsContext();
    const { username, user, clientApi, dispatch: authDispatch } = useAuthContext();

    const axiosInstance = axios.create({
        baseURL: `${clientApi}`,
        headers: {
            'accepts': 'application/json',
            'Content-Type': 'application/json',
        },
    });

    axiosInstance.interceptors.request.use(request => {
        // console.log('Set Bearer Token')
        const accessToken = user.token;
        if (accessToken) {
            request.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return request;
    }, error => {
        return Promise.reject(error);
    });

    axiosInstance.interceptors.response.use(
        response => response, // Directly return successful responses.
        async error => {
            // console.log('onLeave Refresh Access Token Expired!!!')
            const originalRequest = error.config;
            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
                try {
                    if (!isWinAuth) {
                        // Make a request to your auth server to refresh the token.
                        const response = await axiosInstance.post(`${clientApi}api/auth/refresh`, user);
                        // Store the new access and refresh tokens.
                        authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

                        // Update the authorization header with the new access token.
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                    }
                    else {
                        const newToken = await acquireSilentToken();
                        let tmpUser = user;
                        tmpUser.token = newToken;
                        authDispatch({ type: 'REFRESH', payload: tmpUser, clientApi: clientApi, username: username });
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                    }
                    return axios(originalRequest); // Retry the original request with the new access token.
                } catch (refreshError) {
                    // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
                    console.error('Token refresh failed:', refreshError);
                    authDispatch({ type: 'LOGOUT' });
                    return Promise.reject(refreshError);
                }
            }
            return Promise.reject(error); // For all other errors, return the error as is.
        }
    );

    //#region Clients
    const GetClients = async () => {
        setIsLoading(true)
        let resp;
        await axiosInstance.get(`${clientApi}api/clients/GetClientDetails`, {
            headers: { Authorization: `Bearer ${user.token}` },
            params: { Username: username }
        })
            .then((response) => {
                resp = response.data;
                return resp;
            })
            .catch((error) => {
                //console.log(error)
                setError(error)
            })
        setIsLoading(false)
        return resp;
    }

    const InsertClient = async (client) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/clients/InsertClient`;
            const response = await axiosInstance.post(url, client,
                {
                    headers: { Authorization: `Bearer ${user.token}` },
                    params: { username: username },
                });
            setIsLoading(false);
            //console.log(response);
            return response.data;
        } catch (error) {
            //console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const UpdateClient = async (client) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/clients/UpdateClient`;
            const response = await axiosInstance.post(url, client,
                {
                    headers: { Authorization: `Bearer ${user.token}` },
                    params: { username: username },
                });
            setIsLoading(false);
            //console.log(response);
            return response.data;
        } catch (error) {
            //console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const DeleteClient = async (rowguid) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/clients/DeleteClient`;
            const response = await axiosInstance.post(url, null,
                {
                    headers: { Authorization: `Bearer ${user.token}` },
                    params: { username: username, rowguid: rowguid },
                });
            setIsLoading(false);
            //console.log(response);
            return response.data;
        } catch (error) {
            //console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };
    //#endregion

    //#region Projects
    const GetProjects = async (client) => {
        try {
            setIsLoading(true);
            let resp;
            const url = `${clientApi}api/clients/GetClientProjects`;
            const response = await axiosInstance.post(url, client,
                {
                    headers: { Authorization: `Bearer ${user.token}` },
                    params: { username: username },
                });
            setIsLoading(false);
            return response.data;
        } catch (error) {
            //console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    }

    const GetHelpers = async () => {
        setIsLoading(true)
        let resp;
        await axiosInstance.get(`${clientApi}api/clients/GetProjectHelper`, {
            headers: { Authorization: `Bearer ${user.token}` },
            params: { Username: username }
        })
            .then((response) => {
                resp = response.data;
                return resp;
            })
            .catch((error) => {
                //console.log(error)
                setError(error)
            })
        setIsLoading(false)
        return resp;
    }

    const InsertProject = async (project, jobno) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/clients/InsertProject`;
            const response = await axiosInstance.post(url, project,
                {
                    headers: { Authorization: `Bearer ${user.token}` },
                    params: { username: username, jobno: jobno },
                });
            setIsLoading(false);
            //console.log(response);
            return response.data;
        } catch (error) {
            //console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const UpdateProject = async (project) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/clients/UpdateProject`;
            const response = await axiosInstance.post(url, project,
                {
                    headers: { Authorization: `Bearer ${user.token}` },
                    params: { username: username },
                });
            setIsLoading(false);
            //console.log(response);
            return response.data;
        } catch (error) {
            //console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const DeleteProject = async (phasecode) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/clients/DeleteProject`;
            const response = await axiosInstance.post(url, null,
                {
                    headers: { Authorization: `Bearer ${user.token}` },
                    params: { username: username, phasecode: phasecode },
                });
            setIsLoading(false);
            //console.log(response);
            return response.data;
        } catch (error) {
            //console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const DeleteClientEmployeeAuth = async (rowguids) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/clients/DeleteClientEmployeeAuth`;
            const response = await axiosInstance.post(url, rowguids,
                {
                    params: { username: username, },
                });
            setIsLoading(false);
            return response.data;
        } catch (error) {
            console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const SaveClientEmployeeAuth = async (emplClientAuths) => {
        try {
            setIsLoading(true);
            const url = `${clientApi}api/clients/SaveClientEmployeeAuth`;
            const response = await axiosInstance.post(url,
                emplClientAuths,
                { params: { username } }
            );
            setIsLoading(false);
            //console.log(response);
            return response.data;
        } catch (error) {
            console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const GetClientAuthEmployees = async (clientId) => {
        setIsLoading(true)
        let resp;
        await axiosInstance.get(`${clientApi}api/clients/GetClientAuthEmployees`, {
            params: { Username: username, sClient: clientId }
        })
            .then((response) => {
                resp = response.data;
                return resp;
            })
            .catch((error) => {
                console.log(error)
                setError(error)
            })
        setIsLoading(false)
        return resp;
    }
    //#endregion

    return {
        isLoading, error, GetClients, InsertClient, UpdateClient, DeleteClient,
        GetProjects, GetHelpers, InsertProject, UpdateProject, DeleteProject, SaveClientEmployeeAuth, GetClientAuthEmployees,
        DeleteClientEmployeeAuth
    }
}